/** @jsx jsx */
import {
  Flex,
  List,
  ListCellsWrapper,
  ListHeader,
  ListHeaderCell,
  ListItem,
  ListItemCell,
  Paragraph,
} from "@eworkit/iris";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { IContractList } from "@/data/contracts";
import { usePreviousRoutePath } from "@/infrastructure/Route";
import { router } from "@/views/router";

interface ContractsListProps {
  contracts: IContractList[];
}

export const ContractsList: FC<ContractsListProps> = ({ contracts }) => {
  const { t } = useTranslation("session");

  const { currentLocationState } = usePreviousRoutePath();

  const columns = useMemo(() => {
    return {
      project: { sx: { flex: "0 1 10%" } },
      projectStartDate: { sx: { flex: "0 1 12%" } },
      projectEndDate: { sx: { flex: "0 1 12%" } },
      supplierContractId: { sx: { flex: "0 1 15%" } },
      clientName: { sx: { flex: "0 1 13%" } },
      consultant: { sx: { flex: "0 1 18%" } },
      timeReporter: { sx: { flex: "0 1 20%" } },
    };
  }, []);

  return (
    <Flex flexDirection="column" gap="xs">
      <ListHeader>
        <ListCellsWrapper>
          <ListHeaderCell columnConfig={columns.project}>{t("CONTRACTS.LIST.PROJECT")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.projectStartDate}>
            {t("CONTRACTS.LIST.PROJECT_START_DATE")}
          </ListHeaderCell>
          <ListHeaderCell columnConfig={columns.projectEndDate}>{t("CONTRACTS.LIST.PROJECT_END_DATE")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.supplierContractId}>
            {t("CONTRACTS.LIST.SUPPLIER_CONTRACT_ID")}
          </ListHeaderCell>
          <ListHeaderCell columnConfig={columns.clientName}>{t("CONTRACTS.LIST.CLIENT_NAME")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.consultant}>{t("CONTRACTS.LIST.CONSULTANT")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.timeReporter}>{t("CONTRACTS.LIST.TIME_REPORTER")}</ListHeaderCell>
        </ListCellsWrapper>
      </ListHeader>

      <List>
        {contracts.map(contract => (
          <ListItem key={contract.id}>
            <ListCellsWrapper
              as={Link}
              to={{
                pathname: router.get("contracts.details", { contractId: contract.id }).path,
                state: currentLocationState,
              }}>
              <ListItemCell columnConfig={columns.project}>
                <Flex flexDirection="column">
                  <Paragraph bold ellipsis>
                    {contract.projectName}
                  </Paragraph>
                  <Paragraph color="n700">{contract.projectId}</Paragraph>
                </Flex>
              </ListItemCell>
              <ListItemCell columnConfig={columns.projectStartDate}>{contract.projectStartDate}</ListItemCell>
              <ListItemCell columnConfig={columns.projectEndDate}>{contract.projectEndDate}</ListItemCell>
              <ListItemCell columnConfig={columns.supplierContractId}>{contract.supplierContractId}</ListItemCell>
              <ListItemCell columnConfig={columns.clientName}>
                <Flex flexDirection="column">
                  <Paragraph ellipsis>{contract.clientName}</Paragraph>
                </Flex>
              </ListItemCell>
              <ListItemCell columnConfig={columns.consultant}>
                <Flex flexDirection="column">
                  <Paragraph bold ellipsis>
                    {contract.consultantName}
                  </Paragraph>
                  <Paragraph color="n700" ellipsis>
                    {contract.consultantEmail}
                  </Paragraph>
                </Flex>
              </ListItemCell>
              <ListItemCell columnConfig={columns.timeReporter}>
                <Flex flexDirection="column">
                  <Paragraph bold ellipsis>
                    {contract.timeReporterName}
                  </Paragraph>
                  <Paragraph color="n700" ellipsis>
                    {contract.timeReporterEmail}
                  </Paragraph>
                </Flex>
              </ListItemCell>
            </ListCellsWrapper>
          </ListItem>
        ))}
      </List>
    </Flex>
  );
};
